import React, { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { getAdBreaksData } from '~/src/dash/apiV2/inventory-rules/ad-breaks';
import { SearchResultsText } from '~/src/dash/components/input/common';
import { formatDataIntoLVTRows } from '~/src/dash/components/tables/IDNameTable';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  extractDefaultActiveColumnKeys,
  IListViewTableColumnInfo,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import {
  Col,
  Cols,
  Panel,
  PanelBreak,
  Panels,
  Row,
  Rows,
  spacing,
  TextInput,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { adBreaksListConfig } from '~/src/dash/views/inventory-rules-v2/AdBreaks/config';
import { InventoryRulesModal } from '~/src/dash/views/inventory-rules-v2/InventoryRulesModal/InventoryRulesModal';
import { SnackbarView } from '~/src/dash/views/snackbar-view';
import { IAdBreak } from '~/src/dash/apiV2/inventory-rules/ad-breaks';

export const AdBreaksTable = (): JSX.Element => {
  const { isSuperUser, session } = useBaseStore();
  const { t } = usePublicaTranslation();
  const cfg = adBreaksListConfig;
  // table
  const activeColumnKeys = cfg.activeColumnKeys || extractDefaultActiveColumnKeys(cfg.columns);

  const [loadState, setLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [responseData, setResponseData] = useState<IAdBreak[]>([]);
  const [totalRowCount, setTotalRowCount] = useState(0);

  const [query, setQuery] = useState('');

  const [order, setOrder] = useState<Order>(cfg.defaultState.orderDirection || 'asc');
  const [orderBy, setOrderBy] = useState<string>((cfg.defaultState.orderBy as string) || 'ID');
  const [page, setPage] = useState<number>(cfg.defaultState.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(cfg.defaultState.rowsPerPage || 10);
  // AbortController ref for table requests
  const tableAbortControllerRef = useRef<AbortController | null>(null);
  // snackbar
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);
  // modal
  const [activeAdBreak, setActiveAdBreak] = useState<IAdBreak | undefined>(undefined);
  const [isInventoryRulesModalActive, setIsInventoryRulesModalActive] = useState(false);

  const isMounted = useRef(false);

  const handleOnChangeQuery = (text: string): void => {
    unstable_batchedUpdates(() => {
      setQuery(text);
      setPage(0);
    });
  };

  // this sets up the changes to force a re-sort
  const handleOnClickColumnHeader = (column: IListViewTableColumnInfo): void => {
    const { orderByKey, requestKey, defaultOrderDirection } = column;
    const key = orderByKey || requestKey;
    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setPage(0);
      });
    }
  };

  const handleChangePage = (_e: unknown, newPage: number): void => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const updatedRowsPerPage = Number(event.target.value) as RowsPerPage;
    unstable_batchedUpdates(() => {
      setRowsPerPage(updatedRowsPerPage);
      setPage(0);
    });
  };

  const handleClickOnInventoryRulesList = (id: string): void => {
    const selectedAdBreak = responseData.find(
      (adBreak: IAdBreak) => adBreak.ID.toString() === id.toString(),
    );
    unstable_batchedUpdates(() => {
      setActiveAdBreak(selectedAdBreak);
      setIsInventoryRulesModalActive(true);
    });
  };

  const handleCloseModals = (): void => {
    unstable_batchedUpdates(() => {
      setActiveAdBreak(undefined);
      setIsInventoryRulesModalActive(false);
    });
  };

  const fetchTableData = useCallback((): void => {
    if (tableAbortControllerRef.current) {
      tableAbortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    tableAbortControllerRef.current = new AbortController();
    const currentController = tableAbortControllerRef.current;

    setLoadState(LoadingStateLoading);

    const tableQuery = {
      Search: query,
      Page: page,
      PageSize: rowsPerPage,
      OrderBy: orderBy,
      OrderDirection: order,
    };

    getAdBreaksData(session.publisher.ID, tableQuery, currentController.signal)
      .then((response) => {
        unstable_batchedUpdates(() => {
          setLoadState(LoadingStateHasData);
          setResponseData(response.InvRuleAdbreaksData || []);
          setTotalRowCount(response.TotalCount || 0);
        });
      })
      .catch((err: Error) => {
        // Ignore abort errors (they're expected when cancelling requests)
        if (err.name === 'AbortError') {
          return;
        }
        unstable_batchedUpdates(() => {
          setLoadState(LoadingStateNoData);
          setResponseData([]);
          setTotalRowCount(0);
        });
        console.error(err);
      });
  }, [session.publisher.ID, order, orderBy, rowsPerPage, page, query]);

  // Cleanup: abort any in-flight requests when component unmounts
  useEffect(() => {
    isMounted.current = true;
    return () => {
      if (tableAbortControllerRef.current) {
        tableAbortControllerRef.current.abort();
      }
    };
  }, []);

  // Fetch table data when filters or pagination change
  useEffect(() => {
    if (isMounted.current) {
      fetchTableData();
    }
  }, [fetchTableData]);

  const rows = useMemo(
    () => formatDataIntoLVTRows(responseData, cfg.columns),
    [responseData, cfg.columns],
  );

  return (
    <Panels
      max
      vertical
      relative
      largeHorizontalPadding
      verticalPadding
      style={{ rowGap: spacing.medium }}
    >
      <Panel highlighted basePadding>
        <Rows>
          <Row noPadding>
            <Cols wrap gap={spacing.medium} style={{ alignItems: 'center', rowGap: spacing.small }}>
              <Col>
                <TextInput
                  enableClear
                  handleOnChange={handleOnChangeQuery}
                  msDelay={500}
                  placeholder={t('SearchByName')}
                  prefix='search'
                />
              </Col>
              <Col>
                <SearchResultsText
                  numRowsDisplayed={responseData.length}
                  numTotalRows={totalRowCount}
                />
              </Col>
            </Cols>
          </Row>
          <Row noHorizontalPadding verticalPadding>
            <ListViewTable
              activeColumnKeys={activeColumnKeys}
              columns={cfg.columns}
              customFunctions={{
                handleClickOnInventoryRulesList,
              }}
              displayNameWithID
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handleOnClickColumnHeader={handleOnClickColumnHeader}
              isReadAuthorized={true}
              isSuperUser={isSuperUser}
              isWriteAuthorized={true}
              loadState={loadState}
              order={order}
              orderBy={orderBy}
              page={page}
              rows={rows}
              rowsPerPage={rowsPerPage}
              tableName={t('AdBreaks')}
              totalRowCount={totalRowCount}
              className={cfg.cypressClassName}
            />
          </Row>
        </Rows>
      </Panel>
      <InventoryRulesModal
        open={isInventoryRulesModalActive}
        onClose={handleCloseModals}
        inventoryRules={activeAdBreak?.InventoryRules ?? null}
        descriptionTKey='InventoryRulesAdBreakSchedulesModalDescription'
      />
      <SnackbarView
        open={!!snackbarMessage}
        error={!!snackbarError}
        onClose={() => {
          unstable_batchedUpdates(() => {
            setSnackbarMessage('');
            setSnackbarError(false);
          });
        }}
      >
        {snackbarMessage}
      </SnackbarView>
      <PanelBreak height={spacing.large} />
    </Panels>
  );
};
